import React, { memo, useCallback, useState, lazy, Suspense } from 'react'
import { useUser, useAuthStore } from '../store'
import { useAvatarUrl } from '../hooks/useAvatarUrl'
import { useTokenRefresh } from '../hooks'
import { UserProfileEditModal } from './UserProfileEditModal'
import { useUpdateChecker } from '../../hooks/useUpdateChecker'
// 懒加载数据导入/导出弹窗：连带 antd Dialog/Tabs 等重型组件移出首屏 bundle
const DataImportExport = lazy(() => import('./DataImportExport'))
// 懒加载更新弹窗：连带 antd 组件移出首屏 bundle
const UpdateModal = lazy(() => import('@components/UpdateModal/UpdateModal'))
// 懒加载功能意见反馈面板（列表 + 提交表单；主动拉取，无长轮询/无 Electric 订阅）
const FeedbackPanel = lazy(() => import('./FeedbackPanel'))
import { getAppVersion } from '../../services/UpdateService'
import { triggerFullDataResync } from '../../store/collections'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import McpSettingsCard, { isDesktopTauriEnv } from '@components/Mcp/McpSettingsCard'
import './MobileUserMenu.css'

function getInitial(name?: string | null): string {
  if (!name) return '?'
  return name.charAt(0).toUpperCase()
}

interface MobileUserMenuProps {
  onCloseSidebar?: () => void
  /**
   * 渲染形态：
   * - 'sidebar'（默认）：侧边栏底部，显示头像 + 昵称/邮箱
   * - 'header'：桌面端顶栏右上角，仅显示头像（去昵称/邮箱）、高度不超过顶栏行高、右侧留 12px 边距
   */
  variant?: 'sidebar' | 'header'
}

export const MobileUserMenu = memo(function MobileUserMenu({ onCloseSidebar, variant = 'sidebar' }: MobileUserMenuProps) {
  const user = useUser()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [updateModalOpen, setUpdateModalOpen] = useState(false)
  const appVersion = getAppVersion()

  const {
    updateInfo,
    isChecking,
    isUpdating,
    progress,
    downloadStatus,
    error: updateError,
    checkNow,
    dismiss,
    applyUpdate,
    startDownload,
    installDownloaded,
  } = useUpdateChecker({
    checkOnMount: false,
    skipIfCheckedRecently: false,
  })

  const { avatarSrc } = useAvatarUrl(user?.name, user?.avatar)
  useTokenRefresh()
  const logout = useAuthStore((state) => state.logout)

  const openMenu = useCallback(() => {
    setMenuOpen(true)
  }, [])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const handleEditProfile = useCallback(() => {
    setMenuOpen(false)
    setEditModalOpen(true)
  }, [])

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false)
  }, [])

  // 数据导入/导出
  const [importExportOpen, setImportExportOpen] = useState(false)
  const handleImportExport = useCallback(() => {
    setMenuOpen(false)
    setImportExportOpen(true)
  }, [])

  const handleImportExportClose = useCallback(() => {
    setImportExportOpen(false)
  }, [])

  const handleLogout = useCallback(() => {
    setMenuOpen(false)
    onCloseSidebar?.()
    logout()
  }, [logout, onCloseSidebar])

  // ★ 缓存完整性检查：运行本地 ↔ 云端对账并展示结果（与 UserProfile 同源逻辑）
  const [cacheCheckOpen, setCacheCheckOpen] = useState(false)
  const [cacheCheckLoading, setCacheCheckLoading] = useState(false)
  const [cacheCheckResult, setCacheCheckResult] = useState<string>('')
  const handleCacheCheck = useCallback(async () => {
    setMenuOpen(false)
    setCacheCheckLoading(true)
    setCacheCheckResult('')
    try {
      const { runReconciliation } = await import('../../services/cacheReconciliation')
      const result = await runReconciliation(user?.id || '')
      const lost = result?.missing?.length ?? 0
      const extra = result?.extra?.length ?? 0
      setCacheCheckResult(
        `检查完成：本地 ${result?.localCount ?? '-'} 条，云端 ${result?.cloudCount ?? '-'} 条` +
        (lost > 0 || extra > 0 ? `；差异 ${lost} 缺 / ${extra} 多（可用"全量下载"修复）` : '；数据一致 ✓')
      )
    } catch (e) {
      setCacheCheckResult('检查失败：' + (e instanceof Error ? e.message : String(e)))
    } finally {
      setCacheCheckLoading(false)
      setCacheCheckOpen(true)
    }
  }, [user?.id])

  const handleCheckUpdate = useCallback(async () => {
    setMenuOpen(false)
    setUpdateModalOpen(true)
    await checkNow()
  }, [checkNow])

  // ★ 功能意见反馈：底部弹层（打开面板/点击刷新时主动查询，无长轮询）
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const handleOpenFeedback = useCallback(() => {
    setMenuOpen(false)
    setFeedbackOpen(true)
  }, [])

  // ★ 智能体访问（MCP，P1 桌面只读）：仅桌面 Tauri 显示菜单项，弹层展示设置卡片
  const [mcpOpen, setMcpOpen] = useState(false)
  const handleOpenMcp = useCallback(() => {
    setMenuOpen(false)
    setMcpOpen(true)
  }, [])

  const handleUpdateModalClose = useCallback(() => {
    setUpdateModalOpen(false)
    dismiss()
  }, [dismiss])

  // 全量下载（等同冷启动）：确认后清空本地缓存、自动重载应用，启动时从云端全量重新下载
  const [fullResyncDialogOpen, setFullResyncDialogOpen] = useState(false)
  const [fullResyncLoading, setFullResyncLoading] = useState(false)
  const [fullResyncError, setFullResyncError] = useState('')

  const openFullResyncDialog = useCallback(() => {
    setFullResyncError('')
    setMenuOpen(false)
    setFullResyncDialogOpen(true)
  }, [])

  const closeFullResyncDialog = useCallback(() => {
    if (fullResyncLoading) return
    setFullResyncDialogOpen(false)
  }, [fullResyncLoading])

  const executeFullResync = useCallback(async () => {
    if (fullResyncLoading) return
    setFullResyncLoading(true)
    setFullResyncError('')
    try {
      console.log('[MobileUserMenu] 用户触发全量下载')
      await triggerFullDataResync('用户手动触发')
      // 触发成功后应用已重载（triggerFullDataResync 内部调用 window.location.reload()）
      setFullResyncDialogOpen(false)
    } catch (error) {
      console.error('[MobileUserMenu] 全量下载失败:', error)
      setFullResyncError('全量下载失败，请检查网络后重试。')
    } finally {
      setFullResyncLoading(false)
    }
  }, [fullResyncLoading])

  if (!user) {
    return null
  }

  return (
    <>
      {/* 触发按钮 - 侧边栏底部：点击弹出用户菜单；桌面顶栏右上角为紧凑模式（仅头像） */}
      <button
        className={`mobile-user-menu-trigger${variant === 'header' ? ' mobile-user-menu-trigger--header' : ''}`}
        onClick={openMenu}
        aria-label="用户菜单"
        type="button"
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={user.name || user.email || ''}
            className={`mobile-user-menu-trigger__avatar${variant === 'header' ? ' mobile-user-menu-trigger__avatar--header' : ''}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const parent = target.parentElement
              if (parent) {
                parent.classList.add('mobile-user-menu-trigger--fallback')
                parent.textContent = getInitial(user.name)
              }
            }}
          />
        ) : (
          <div className={`mobile-user-menu-trigger__placeholder${variant === 'header' ? ' mobile-user-menu-trigger__placeholder--header' : ''}`}>
            {getInitial(user.name)}
          </div>
        )}
        {/* 侧边栏模式显示昵称/邮箱；顶栏右上角紧凑模式不显示 */}
        {variant !== 'header' && (
          <div className="mobile-user-menu-trigger__info">
            <div className="mobile-user-menu-trigger__name">
              {user.nickname || user.name || '未命名用户'}
            </div>
            <div className="mobile-user-menu-trigger__email">
              {user.email || '无邮箱'}
            </div>
          </div>
        )}
      </button>

      {/* 底部弹出菜单 */}
      {menuOpen && (
        <div className="mobile-user-menu-overlay" onClick={closeMenu}>
          <div
            className="mobile-user-menu-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-user-menu-handle" />

            {/* ★ 菜单顶部头像/昵称：点击进入个人资料编辑弹窗 */}
            <button
              type="button"
              className="mobile-user-menu-header"
              onClick={handleEditProfile}
              aria-label="编辑个人资料"
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={user.name || user.email || ''}
                  className="mobile-user-menu-header__avatar"
                />
              ) : (
                <div className="mobile-user-menu-header__avatar-placeholder">
                  {getInitial(user.name)}
                </div>
              )}
              <div className="mobile-user-menu-header__info">
                <div className="mobile-user-menu-header__name">
                  {user.nickname || user.name || '未命名用户'}
                </div>
                <div className="mobile-user-menu-header__email">
                  {user.email || '无邮箱'}
                </div>
              </div>
            </button>

            <div className="mobile-user-menu-divider" />

            <div className="mobile-user-menu-items">
              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={handleEditProfile}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>我的帐号</span>
              </button>

              {/* 数据导入/导出：桌面端与移动端（Android，SAF 文件选择）均可用 */}
              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={handleImportExport}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>数据导入/导出</span>
              </button>

              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={openFullResyncDialog}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 17l3 3m0 0l3-3m-3 3V12"
                  />
                </svg>
                <span>全量下载</span>
              </button>

              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={handleCheckUpdate}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>版本与更新</span>
                <span className="mobile-user-menu-item__version">v{appVersion}</span>
              </button>

              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={handleCacheCheck}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                <span>缓存完整性检查</span>
              </button>

              <button
                type="button"
                className="mobile-user-menu-item"
                onClick={handleOpenFeedback}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
                  />
                </svg>
                <span>功能意见反馈</span>
              </button>

              {/* ★ 智能体访问（MCP）：仅桌面 Tauri 环境显示（Mac/Windows 桌面端实际入口） */}
              {isDesktopTauriEnv() && (
                <button
                  type="button"
                  className="mobile-user-menu-item"
                  onClick={handleOpenMcp}
                >
                  <svg
                    className="mobile-user-menu-item__icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                    />
                  </svg>
                  <span>智能体访问（MCP）</span>
                </button>
              )}

              <button
                type="button"
                className="mobile-user-menu-item mobile-user-menu-item--danger"
                onClick={handleLogout}
              >
                <svg
                  className="mobile-user-menu-item__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>退出登录</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <UserProfileEditModal
          onClose={handleEditModalClose}
        />
      )}

      {importExportOpen && (
        <Suspense fallback={null}>
          <DataImportExport
            open={importExportOpen}
            onClose={handleImportExportClose}
          />
        </Suspense>
      )}

      {/* ★ 缓存完整性检查结果 */}
      {cacheCheckOpen && (
        <div className="mobile-user-menu-overlay" onClick={() => setCacheCheckOpen(false)}>
          <div
            className="mobile-user-menu-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '20px 16px' }}
          >
            <div className="mobile-user-menu-handle" />
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
              {cacheCheckLoading ? '正在检查缓存完整性...' : '缓存完整性检查'}
            </h3>
            {cacheCheckLoading ? (
              <p style={{ color: '#888', fontSize: 14 }}>正在比对本地与云端数据…</p>
            ) : (
              <p style={{ fontSize: 14, lineHeight: 1.6, color: cacheCheckResult.includes('一致') ? '#16a34a' : '#d97706' }}>
                {cacheCheckResult}
              </p>
            )}
            <button
              type="button"
              onClick={() => setCacheCheckOpen(false)}
              style={{
                marginTop: 16, width: '100%', padding: '10px 0', borderRadius: 8,
                background: '#f3f4f6', fontSize: 14, fontWeight: 500,
              }}
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {/* ★ 功能意见反馈面板（主动拉取：打开时查询一次，无长轮询） */}
      {feedbackOpen && (
        <Suspense fallback={null}>
          <FeedbackPanel onClose={() => setFeedbackOpen(false)} />
        </Suspense>
      )}

      {/* ★ 智能体访问（MCP）设置弹层：卡片自包含检测，非桌面 Tauri 渲染 null */}
      {mcpOpen && (
        <div className="mobile-user-menu-overlay" onClick={() => setMcpOpen(false)}>
          <div
            className="mobile-user-menu-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '20px 16px' }}
          >
            <div className="mobile-user-menu-handle" />
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <McpSettingsCard />
            </div>
            <button
              type="button"
              onClick={() => setMcpOpen(false)}
              style={{
                marginTop: 16, width: '100%', padding: '10px 0', borderRadius: 8,
                background: '#f3f4f6', fontSize: 14, fontWeight: 500,
              }}
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {updateModalOpen && (
        <Suspense fallback={null}>
          <UpdateModal
            visible={updateModalOpen}
            updateInfo={updateInfo}
            isUpdating={isUpdating}
            isChecking={isChecking}
            progress={progress}
            error={updateError}
            downloadStatus={downloadStatus}
            onClose={handleUpdateModalClose}
            onUpdate={applyUpdate}
            onCheckNow={checkNow}
            onDismiss={dismiss}
            onStartDownload={startDownload}
            onInstallDownloaded={installDownloaded}
            onRetryDownload={startDownload}
          />
        </Suspense>
      )}

      <ConfirmDialog
        open={fullResyncDialogOpen}
        title="全量下载"
        content={
          <>
            <p style={{ margin: 0 }}>
              全量下载将清空本地缓存并从云端重新下载全部数据（笔记与块引用，等同冷启动），应用会自动重载，期间显示"正在全量下载云端数据…"，确定继续吗？
            </p>
            {fullResyncError && (
              <p style={{ margin: '8px 0 0', color: '#dc2626', fontSize: '13px' }}>
                {fullResyncError}
              </p>
            )}
          </>
        }
        confirmText="开始全量下载"
        cancelText="取消"
        danger
        loading={fullResyncLoading}
        onConfirm={executeFullResync}
        onCancel={closeFullResyncDialog}
      />
    </>
  )
})

export default MobileUserMenu
